import React from 'react';

import { Typography } from '@mui/material';
import { TFunction } from 'i18next';

import HumanizerGoalAnimation from '~/modules/feature-access/components/HumanizerGoalAnimation';
import InputLimitOverflowAnimation from '~/modules/feature-access/components/InputLimitOverflowAnimation';
import IntroAnimation from '~/modules/feature-access/components/IntroAnimation';
import { IntroAnimationVariant } from '~/modules/feature-access/components/IntroAnimation/types';
import ParaphraserLevelAnimation from '~/modules/feature-access/components/ParaphraserLevelAnimation';
import PlagiarismReportAnimation from '~/modules/feature-access/components/PlagiarismReportAnimation';
import {
  BenefitItemType,
  FeatureKey,
  FeatureModule,
} from '~/modules/feature-access/policy';
import { getDetectorInputLimits } from '~/modules/tools/views/AiDetectorV4/helpers';
import { getPlagiarismInputLimits } from '~/modules/tools/views/PlagiarismCheckerV4/helpers';
import { Typographies } from '~/theme/typography';

import { DEFAULT_PLAGIARISM_RESULT } from './constants';
import styles from './styles.module.scss';

export const getBenefitsList = (t: TFunction): BenefitItemType[] => [
  {
    key: null,
    module: null,
    text: t(
      'modal:feature_unlock.benefits[0]',
      'Unlimited words for all tools',
    ),
  },
  {
    key: FeatureKey.DETECTOR_DETAILED_REPORT,
    module: FeatureModule.AI_CONTENT_DETECTOR,
    text: t(
      'modal:feature_unlock.benefits[1]',
      'Detailed AI Detection Reports',
    ),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.benefits[1]',
            'Detailed AI Detection Reports',
          )}
        </Typography>

        <div className={styles.preview__animation}>
          <IntroAnimation variant={IntroAnimationVariant.DETECTOR_REPORT} />
        </div>

        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__subtitle}
        >
          {t(
            'modal:feature_unlock.detector_report_subtitle',
            'Your digital proof of academic integrity',
          )}
        </Typography>
      </div>
    ),
  },
  {
    key: FeatureKey.DETECTOR_INPUT_HIGHLIGHTS,
    module: FeatureModule.AI_CONTENT_DETECTOR,
    text: t('modal:feature_unlock.benefits[2]', 'AI Sentences Highlight'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t('modal:feature_unlock.benefits[2]', 'AI Sentences Highlight')}
        </Typography>

        <div className={styles.preview__animation}>
          <IntroAnimation variant={IntroAnimationVariant.DETECTOR_HIGHLIGHTS} />
        </div>

        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__subtitle}
        >
          {t(
            'modal:feature_unlock.detector_highlights_subtitle',
            'Reliably get rid of AI in text with minimal changes. Get more control over how text is humanized.',
          )}
        </Typography>
      </div>
    ),
  },
  {
    key: FeatureKey.DETECTOR_INPUT_LIMIT_OVERFLOW,
    module: FeatureModule.AI_CONTENT_DETECTOR,
    text: t(
      'modal:feature_unlock.benefits[3]',
      'Extra Long AI Detection Input',
    ),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.maximum_ai_detection_input',
            'Maximum Ai Detection Input',
          )}
        </Typography>

        <div className={styles.preview__animation}>
          <InputLimitOverflowAnimation
            increaser={Math.floor(
              getDetectorInputLimits({ isNotPremium: false }).maxCount /
                getDetectorInputLimits({ isNotPremium: true }).maxCount,
            )}
            inputLimit={getDetectorInputLimits({ isNotPremium: true }).maxCount}
            inputCurrent={
              getDetectorInputLimits({ isNotPremium: false }).maxCount
            }
          />
        </div>

        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__subtitle}
        >
          {t('modal:feature_unlock.input_limit_overflow_subtitle', {
            max_input_words: getDetectorInputLimits({ isNotPremium: false })
              .maxCount,
            defaultValue: 'Process up to {{max_input_words}} per request',
          })}
        </Typography>
      </div>
    ),
  },
  {
    key: FeatureKey.PLAGIARISM_DETAILED_REPORT,
    module: FeatureModule.PLAGIARISM_CHECK,
    text: t('modal:feature_unlock.benefits[4]', 'Detailed Plagiarism Reports'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t('modal:feature_unlock.benefits[4]', 'Detailed Plagiarism Reports')}
        </Typography>

        <div className={styles.preview__animation}>
          <PlagiarismReportAnimation
            plagiarismResult={DEFAULT_PLAGIARISM_RESULT}
          />
        </div>

        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__subtitle}
        >
          {t(
            'modal:feature_unlock.plagiarism_report_subtitle',
            'Your digital proof of academic integrity',
          )}
        </Typography>
      </div>
    ),
  },
  {
    key: FeatureKey.PLAGIARISM_INPUT_LIMIT_OVERFLOW,
    module: FeatureModule.PLAGIARISM_CHECK,
    text: t('modal:feature_unlock.benefits[5]', 'Extra Long Plagiarism Input'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.maximum_plagiarism_input',
            'Maximum Plagiarism Input',
          )}
        </Typography>

        <div className={styles.preview__animation}>
          <InputLimitOverflowAnimation
            increaser={Math.floor(
              getPlagiarismInputLimits({
                isNotPremium: false,
                isAnnualSub: true,
              }).maxCount /
                getPlagiarismInputLimits({
                  isNotPremium: true,
                  isAnnualSub: false,
                }).maxCount,
            )}
            inputLimit={
              getPlagiarismInputLimits({
                isNotPremium: true,
                isAnnualSub: false,
              }).maxCount
            }
            inputCurrent={
              getPlagiarismInputLimits({
                isNotPremium: false,
                isAnnualSub: true,
              }).maxCount
            }
          />
        </div>

        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__subtitle}
        >
          {t('modal:feature_unlock.input_limit_overflow_subtitle', {
            max_input_words: getPlagiarismInputLimits({
              isNotPremium: false,
              isAnnualSub: true,
            }).maxCount,
            defaultValue: 'Process up to {{max_input_words}} per request',
          })}
        </Typography>
      </div>
    ),
  },
  {
    key: FeatureKey.HUMANIZER_GOAL,
    module: FeatureModule.AI_TEXT_HUMANIZER,
    text: t('modal:feature_unlock.benefits[6]', 'Humanization Goal Settings'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t('modal:feature_unlock.benefits[6]', 'Humanization Goal Settings')}
        </Typography>

        <HumanizerGoalAnimation />
      </div>
    ),
  },
  {
    key: FeatureKey.PARAPHRASER_TONE_OF_VOICE,
    module: FeatureModule.PARAPHRASE_TEXT,
    text: t('modal:feature_unlock.benefits[7]', 'Tone Of Voice Selection'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.tone_of_voice_setting',
            'Tone Of Voice Setting',
          )}
        </Typography>

        <div className={styles.preview__animation}>
          <IntroAnimation variant={IntroAnimationVariant.PARAPHRASE_TONE} />
        </div>
      </div>
    ),
  },
  {
    key: FeatureKey.PARAPHRASER_OUTPUT_COUNTER,
    module: FeatureModule.PARAPHRASE_TEXT,
    text: t('modal:feature_unlock.benefits[8]', 'Up to 5 Outputs per request'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.multiple_output_variants',
            'Multiple Output Variants ',
          )}
        </Typography>

        <div className={styles.preview__animation}>
          <IntroAnimation variant={IntroAnimationVariant.PARAPHRASE_OUTPUT} />
        </div>
      </div>
    ),
  },
  {
    key: FeatureKey.PARAPHRASER_LEVEL,
    module: FeatureModule.PARAPHRASE_TEXT,
    text: t('modal:feature_unlock.benefits[9]', 'Set Paraphrase Levels'),
    PreviewComponent: () => (
      <div className={styles.preview}>
        <Typography
          variant={Typographies.LABEL_LARGE}
          component="div"
          className={styles.preview__title}
        >
          {t(
            'modal:feature_unlock.paraphrase_level_settings[9]',
            'Paraphrase Level Settings',
          )}
        </Typography>

        <ParaphraserLevelAnimation />
      </div>
    ),
  },
  {
    key: null,
    module: null,
    text: t(
      'modal:feature_unlock.benefits[10]',
      'Early access to new AI models and features',
    ),
  },
  {
    key: null,
    module: null,
    text: t('modal:feature_unlock.benefits[11]', 'Unlimited image generations'),
  },
  {
    key: null,
    module: null,
    text: t(
      'modal:feature_unlock.benefits[12]',
      'Lifetime access to the browser extension',
    ),
  },
];
